import { useEffect, useState } from 'react';
import { ExternalLink, RefreshCw, ShieldCheck, Sparkles } from 'lucide-react';
import { Alert, Badge, Button, Card, Empty, Loading, PageHeader } from '../components/UI';
import { api, errorText } from '../lib/api';

type CohortOption = { uuid: string; name: string; code?: string };
type UserRef = { uuid: string; name?: string; email?: string };
type LearnerRow = { uuid: string; user?: UserRef };
type Preview = { token: string; code: string; preview_url: string; portal_url: string; user: UserRef };

const STEPS: Array<[string, string]> = [
  ['Dashboard', 'The learner lands on their overview: active programme, next class, progress and outstanding work.'],
  ['Live classes', 'Join only opens inside the 1-hour window; the check-in code appears when attendance capture is open.'],
  ['Attendance check-in', 'Enter the class PIN and watch the attendance status change to present or late.'],
  ['My learning', 'Read the week\'s lesson, open the attached materials (PDF/slides/documents) and mark the class done.'],
  ['Assessments', 'Start a quiz, answer, submit, and see the result and feedback.'],
  ['Assignments', 'Open the brief, submit work, and later see the grade and feedback.'],
  ['Recordings', 'Watch and download a published class recording served from the LMS.'],
  ['Records & certificate', 'Check progression, the attendance record and (when eligible) the certificate.'],
];

export function LearnerPreviewPage() {
  const [cohorts, setCohorts] = useState<CohortOption[]>([]);
  const [cohortUuid, setCohortUuid] = useState('');
  const [learners, setLearners] = useState<LearnerRow[]>([]);
  const [learnerUuid, setLearnerUuid] = useState('');
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingLearners, setLoadingLearners] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadCohorts = async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get<unknown>('/admin/cohorts/options');
      const rows = Array.isArray(r.data) ? r.data as CohortOption[] : ((r.data as { data?: CohortOption[] })?.data || []);
      setCohorts(rows);
      if (!cohortUuid && rows[0]) setCohortUuid(rows[0].uuid);
    } catch (e) { setError(errorText(e)); } finally { setLoading(false); }
  };

  const loadLearners = async () => {
    if (!cohortUuid) { setLearners([]); return; }
    setLoadingLearners(true); setError('');
    try {
      const r = await api.get<unknown>(`/admin/enrollments?cohort_uuid=${encodeURIComponent(cohortUuid)}`);
      const rows = (Array.isArray(r.data) ? r.data : ((r.data as { data?: LearnerRow[] })?.data || [])).filter((row: LearnerRow) => row.user);
      setLearners(rows);
      setLearnerUuid(prev => (prev && rows.some(row => row.user?.uuid === prev) ? prev : (rows[0]?.user?.uuid || '')));
    } catch (e) { setError(errorText(e)); } finally { setLoadingLearners(false); }
  };

  useEffect(() => { void loadCohorts(); }, []);
  useEffect(() => { void loadLearners(); }, [cohortUuid]);

  const current = learners.find(row => row.user?.uuid === learnerUuid)?.user || null;

  const openPreview = async () => {
    if (!learnerUuid) { setError('Select a learner first.'); return; }
    setBusy('open'); setError('');
    try {
      const r = await api.post<Preview>(`/admin/users/${learnerUuid}/impersonate`);
      setMessage(`Preview started for ${r.data.user?.name || 'the learner'}. The portal is opening in a new tab.`);
      window.open(r.data.preview_url, '_blank', 'noopener');
    } catch (e) { setError(errorText(e)); } finally { setBusy(''); }
  };

  return (
    <div className="page-stack">
      <PageHeader eyebrow="Learner preview" title="Walk through the student experience"
        description="Open the portal as a real learner and click through the whole journey exactly as a student sees it."
        actions={<Button kind="secondary" onClick={() => void loadCohorts()}><RefreshCw /> Refresh</Button>} />
      {message && <Alert type="success" onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert onClose={() => setError('')}>{error}</Alert>}

      <section className="lp-hero">
        <div className="lp-hero-copy">
          <span className="lp-eyebrow"><Sparkles size={14} /> Preview as a learner</span>
          <h2>Open the portal as a learner</h2>
          <p>Pick a cohort and a learner, then open the portal signed in as them. A banner at the top of the portal lets you exit the preview at any time.</p>
        </div>
        <div className="lp-picker">
          <label className="lp-field"><span>Cohort</span>
            <select value={cohortUuid} onChange={e => setCohortUuid(e.target.value)} disabled={loading}>
              {cohorts.map(c => <option key={c.uuid} value={c.uuid}>{c.name}{c.code ? ` (${c.code})` : ''}</option>)}
            </select>
          </label>
          <label className="lp-field"><span>Learner</span>
            <select value={learnerUuid} onChange={e => setLearnerUuid(e.target.value)} disabled={loadingLearners || learners.length === 0}>
              <option value="">{loadingLearners ? 'Loading learners…' : learners.length ? 'Select a learner' : 'No enrolled learners'}</option>
              {learners.map(row => <option key={row.user!.uuid} value={row.user!.uuid}>{row.user!.name} — {row.user!.email}</option>)}
            </select>
          </label>
          <Button busy={busy === 'open'} disabled={!learnerUuid} onClick={() => void openPreview()}><ExternalLink /> Open portal as learner</Button>
        </div>
        {current
          ? <p className="lp-selected">You will open the portal as <strong>{current.name}</strong> ({current.email}).</p>
          : (!loading && !loadingLearners && learners.length === 0 && <p className="lp-selected">No learners are enrolled in this cohort yet — enrol a learner first.</p>)}
      </section>

      <Card title="What to walk through" action={<Badge>{STEPS.length} steps</Badge>}>
        <ol className="lp-steps">
          {STEPS.map(([title, text], index) => (
            <li key={title}>
              <span className="lp-step-num">{index + 1}</span>
              <div><strong>{title}</strong><small>{text}</small></div>
            </li>
          ))}
        </ol>
      </Card>

      <p className="lp-note"><ShieldCheck size={14} /> Preview links last 30 minutes and are audited. Only super admins and training directors can start a preview, and only learner accounts can be previewed.</p>
    </div>
  );
}
